import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth, googleProvider, signInWithPopup, RecaptchaVerifier, signInWithPhoneNumber, signOut } from '../firebase';
import { useAuth } from '../context/AuthContext';

export const LoginPage = () => {
    const [phone, setPhone] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState('options');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [confirmResult, setConfirmResult] = useState(null);
    const recaptchaRef = useRef(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        if (user) navigate('/');
    }, [user, navigate]);

    const setupRecaptcha = () => {
        if (!window.recaptchaVerifier) {
            window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                size: 'invisible',
                callback: () => {}
            });
        }
    };

    const handleGoogle = async () => {
        setLoading(true);
        setError('');
        try {
            await signInWithPopup(auth, googleProvider);
            navigate('/');
        } catch (err) {
            setError('Google sign-in failed. Try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleSendOTP = async () => {
        if (!phone || phone.length < 10) { setError('Enter a valid phone number'); return; }
        setLoading(true);
        setError('');
        try {
            setupRecaptcha();
            const result = await signInWithPhoneNumber(auth, `+91${phone}`, window.recaptchaVerifier);
            setConfirmResult(result);
            setStep('otp');
        } catch (err) {
            setError('Failed to send OTP. Try again.');
            window.recaptchaVerifier = null;
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        if (!otp || otp.length !== 6) { setError('Enter a valid 6-digit OTP'); return; }
        setLoading(true);
        setError('');
        try {
            await confirmResult.confirm(otp);
            navigate('/');
        } catch (err) {
            setError('Invalid OTP. Try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div id="recaptcha-container"></div>
            <div className="w-full max-w-md">
                <Link to="/" className="font-heading text-3xl mb-8 block text-center">
                    <span className="text-lime">YES</span>suhchill
                </Link>
                <div className="bg-card border border-border p-8">
                    <h1 className="font-heading text-2xl tracking-wider mb-2 text-center">SIGN IN</h1>
                    <p className="text-muted-foreground text-sm text-center mb-8">Access your account</p>

                    {error && <p className="text-red-400 text-sm mb-4 text-center">{error}</p>}

                    {step === 'options' && (
                        <>
                            <button onClick={handleGoogle} disabled={loading}
                                className="w-full flex items-center justify-center gap-3 border border-border py-3 px-4 mb-4 hover:border-lime hover:text-lime transition-colors text-sm font-medium">
                                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                                Continue with Google
                            </button>
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                                <div className="relative flex justify-center text-xs text-muted-foreground"><span className="bg-card px-3">OR</span></div>
                            </div>
                            <div className="flex gap-2 mb-4">
                                <span className="bg-card border border-border px-3 py-3 text-sm text-muted-foreground">+91</span>
                                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                                    placeholder="Mobile number"
                                    className="flex-1 bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime" />
                            </div>
                            <button onClick={handleSendOTP} disabled={loading}
                                className="w-full bg-lime text-black py-3 text-xs font-bold tracking-[0.14em] uppercase hover:bg-lime/90 transition-colors disabled:opacity-50">
                                {loading ? 'SENDING...' : 'SEND OTP'}
                            </button>
                        </>
                    )}

                    {step === 'otp' && (
                        <>
                            <p className="text-sm text-muted-foreground text-center mb-6">OTP sent to +91 {phone}</p>
                            <input type="text" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                placeholder="Enter 6-digit OTP"
                                className="w-full bg-card border border-border px-4 py-3 text-sm focus:outline-none focus:border-lime mb-4 text-center tracking-widest text-lg" />
                            <button onClick={handleVerifyOTP} disabled={loading}
                                className="w-full bg-lime text-black py-3 text-xs font-bold tracking-[0.14em] uppercase hover:bg-lime/90 transition-colors disabled:opacity-50 mb-3">
                                {loading ? 'VERIFYING...' : 'VERIFY OTP'}
                            </button>
                            <button onClick={() => { setStep('options'); setOtp(''); setError(''); }}
                                className="w-full text-sm text-muted-foreground hover:text-lime transition-colors">
                                ← Change number
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
